/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, { FC, useEffect, useMemo, useRef, useState, useCallback } from 'react';
import Backdrop from '@mui/material/Backdrop';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import { Theme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import TwMap from '../TwMap/TwMap';
import TwMarker, {
  LiveLocationMarker,
  LocationMarker,
  LocationMarkerType
} from '../TwMarker/TwMarker';
import useDirections from '../../hooks/useGoogleDirections';
import useAuth from '../../providers/Auth/Auth.provider';
import useGoogleMaps from '../../providers/GoogleMaps/GoogleMaps.provider';
import { LOAD_TYPE, LoadLocation } from '../../redux/api/load/load.model';
import useFeatureFlags from '../../providers/FeatureFlags/FeatureFlags.provider';
import { getLiveLocationMarker, getMapMarkers, getMarkerIcon } from '../../utils/mapUtils';
import useNotifications from '../../providers/Notifications/Notifications.provider';
import { PROVIDER, USER_ROLE } from '../../providers/User/User.model';
import UploadBol from '../../pages/UploadBol/UploadBol';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import {
  setEntityOverlayInitialTabId,
  setLoadDetailReload,
  setTripDetailReload
} from '../../redux/features/navigation/navigationSlice';
import { setSelectedTripId } from '../../redux/features/orders/ordersSlice';

import { skipToken } from '@reduxjs/toolkit/dist/query';
import { useGetEquipmentByIdQuery } from '../../redux/api/equipment/equipmentApi';
import DateUtils from '../../utils/dateUtils';
import TwLiveTrackingAlert, {
  LiveEntityType,
  OrderTripLiveStatus
} from '../TwLiveTrackingAlert/TwLiveTrackingAlert';
import { getLiveStatus, getLiveTrackEntity } from '../../utils/liveTrackingUtils';
import { ClientResponse } from '../../redux/api/customer/customer.model';
import { TabPanel } from '../TwTabPanel/TwTabPanel';
import HeaderOverlay from './HeaderOverlay/HeaderOverlay';
import OverlayTabs from './OverlayTabs/OverlayTabs';
import OrderTab from './TabsContent/OrderTab';

import { Trip, TRIP_STATUS, TripStop } from '../../redux/api/trips/trips.model';
import { CLIENT_TYPE, Order, ORDER_STATUS } from '../../redux/api/orders/orders.model';
import '../TwLoadDetail/TwLoadDetail.scss';
import { EntityType } from '../../redux/Global.model';
import TripTab from './TabsContent/TripTab';
import LoadsService from '../../providers/Loads/Loads.service';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';

const DEFAULT_MAP_CENTER = { lat: 39.7567241, lng: -101.6269877 };
const MAX_STOP_MARKER_COUNT = 25;

interface TwEntityOverlayProps {
  entityId?: string;
  entityType?: EntityType;
  open: boolean;
  shareOrderSuccess?: boolean;
  shareTripSuccess?: boolean;
  readonly?: boolean;
  forceOnlyEdit?: boolean;
  onClose?: () => void;
  onTripStatusChange?: () => void;
  onOrderStatusChange?: () => void;
  onSelectedTripChange?: (segmentId: string) => void; // For map marker click?
  onSelectedOrderChange?: (orderId: string) => void; // For map marker click?
  selectedTripId?: string; // autoselect trip tab when overlay opens
  selectedOrderId?: string; // autoselect order tab when overlay opens
}

// Add styled components at the top level
const StyledTabsContainer = styled(Box)({
  '.MuiTabs-root': {
    minHeight: '48px',
    '.MuiTabs-scroller': {
      '.MuiTabs-flexContainer': {
        gap: '8px',
        padding: '0 16px',
        '.MuiTab-root': {
          minWidth: 'unset',
          padding: '8px 16px',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: '200px',
          minHeight: '36px'
        }
      }
    }
  }
});

const TwEntityOverlay: FC<TwEntityOverlayProps> = ({
  entityId,
  entityType,
  open,
  readonly,
  forceOnlyEdit,
  onClose,
  onOrderStatusChange,
  onTripStatusChange,
  onSelectedTripChange,
  onSelectedOrderChange,
  selectedTripId,
  selectedOrderId
}) => {
  const dispatch = useAppDispatch();
  const { loaded: googleMapsLoaded } = useGoogleMaps();
  const { enableDynamicWebTwain, enableLiveTracking, enableHideGoogleMapsAndRoutingCalculations } =
    useFeatureFlags();
  const { addNotification } = useNotifications();
  const {
    data: { userData },
    getTenantSetting
  } = useAuth();
  const enableLtl = userData?.tenant?.ltl_enabled || false;
  const mapRef = useRef<HTMLDivElement | null>(null);
  const isRequireBolActive = getTenantSetting('require-bol');
  const matchesMobileOrTablet = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'));
  const showMapToDrivers = getTenantSetting('show-route-map-to-drivers');

  // Overlay reload
  const loadDetailReload = useAppSelector((state) => state.navigation.loadDetailReload);
  const tripDetailReload = useAppSelector((state) => state.navigation.tripDetailReload);

  const entityOverlayInitialTabId = useAppSelector(
    (state) => state.navigation.entityOverlayInitialTabId
  );

  const [order, setOrder] = useState<Order | null>(null);
  const [trip, setTrip] = useState<Trip | null>(null);
  const [isOrderLoading, setIsOrderLoading] = useState<boolean>(false);
  const [isTripLoading, setIsTripLoading] = useState<boolean>(false);
  const [showMap, setShowMap] = useState<boolean>(false);
  const [map, setMap] = useState<google.maps.Map>();
  const [markers, setMarkers] = useState<LocationMarker[]>([]);
  const [primaryClient, setPrimaryClient] = useState<ClientResponse | null>(null);
  const [isTwScannerOpen, setIsTwScannerOpen] = useState<boolean>(false);
  const [uploadBolOpen, setUploadBolOpen] = useState<boolean>(false);

  const [currentTab, setCurrentTab] = useState<number>(0);
  const [stops, setStops] = useState<TripStop[]>([]);
  const [displayOrders, setDisplayOrders] = useState<Order[]>([]); // IF OVERLAY RENDERS TRIP
  const [displayTrips, setDisplayTrips] = useState<Trip[]>([]); // IF OVERLAY RENDERS AN ORDER

  // live tracking states
  const [currentTruckId, setCurrentTruckId] = useState<string | undefined | null>(undefined);
  const [liveMarkers, setLiveMarkers] = useState<LiveLocationMarker[]>([]);
  const [liveStatus, setLiveStatus] = useState<OrderTripLiveStatus>(OrderTripLiveStatus.notStarted);
  const [liveTrackedEntity, setLiveTrackedEntity] = useState(LiveEntityType.order);

  const isOrder = useMemo(() => {
    return entityType === EntityType.ORDER && !!entityId;
  }, [entityType, entityId]);
  const isTrip = useMemo(() => {
    return entityType === EntityType.TRIP && !!entityId;
  }, [entityType, entityId]);

  const isDriver = userData?.role === USER_ROLE.DRIVER;
  const isCarrier = userData?.role === USER_ROLE.CARRIER;
  const isCustomerContact = userData?.role === USER_ROLE.CUSTOMER_CONTACT;
  const isDriverOrCarrier = isDriver || isCarrier;

  useEffect(() => {
    if (!isDriver) setShowMap(true);
    else if (isDriver && showMapToDrivers) {
      setShowMap(true);
    }
  }, [isDriver, showMapToDrivers]);

  const { data: truck, refetch: refetchEquipment } = useGetEquipmentByIdQuery(
    currentTruckId ?? skipToken,
    {
      skip: !currentTruckId
    }
  );

  const usesPcMiler = useMemo((): boolean => {
    return !!displayTrips.find(
      (trip) =>
        trip.loaded_mileage_source === PROVIDER.PC || trip.empty_mileage_source === PROVIDER.PC
    );
  }, [displayTrips]);

  const hasInitialTabChanged = useRef(false);

  const getOrderData = (orderId?: string): void => {
    setIsOrderLoading(true);
    try {
      LoadsService.getOrderById(orderId || entityId || '')
        .then((response) => {
          setOrder(response);
          if (!enableLtl && entityOverlayInitialTabId) {
            dispatch(setTripDetailReload(true));
          }
        })
        .catch(() => {
          addNotification({
            message: 'Failed to load order details, please try again',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsOrderLoading(false);
        });
    } catch (e) {
      addNotification({
        message: 'Failed to load order details, please try again',
        variant: 'error'
      });
    }
  };

  const getTripData = (tripId?: string): void => {
    setIsTripLoading(true);
    try {
      LoadsService.getTripById(tripId || entityId || '')
        .then((response) => {
          setTrip(response);
          // update trips when tripId is provided (FTL)
          if (tripId) setDisplayTrips(isDriverOrCarrier ? filterTrips([response]) : [response]);
        })
        .catch(() => {
          addNotification({
            message: 'Failed to load trip details, please try again',
            variant: 'error'
          });
        })
        .finally(() => {
          setIsTripLoading(false);
        });
    } catch (e) {
      addNotification({
        message: 'Failed to load trip details, please try again',
        variant: 'error'
      });
    }
  };

  useEffect(() => {
    if (open) {
      if (isOrder) {
        getOrderData();
      } else if (isTrip) {
        getTripData();
      }
    }
  }, [entityId, entityType, open]);

  useEffect(() => {
    if (!hasInitialTabChanged.current) {
      if (isTrip && displayOrders && displayOrders.length > 0) {
        if (isDriver && enableLtl) {
          // this isDriver and enableLtl is a quick fix because the entityOverlayInitialTabId is not set from active trips (not sure if that's a bug) and I couldn't find an easy way to find the right index otherwise so I'm assuming we'd show the trip at the end for driver and LTL based on what's in the overlaytabs
          setCurrentTab(displayOrders.length);
          hasInitialTabChanged.current = true;
        } else {
          const orderIndex = displayOrders.findIndex(
            (order) => order.id === entityOverlayInitialTabId
          );
          if (orderIndex >= 0) {
            setCurrentTab(orderIndex + 1);
            hasInitialTabChanged.current = true;
          }
        }
        // find index of the selected order
      } else if (isOrder && displayTrips && displayTrips.length > 0) {
        // find index of the selected trip
        const tripIndex = displayTrips.findIndex((trip) => trip.id === entityOverlayInitialTabId);
        if (tripIndex >= 0) {
          setCurrentTab(tripIndex + 1);
          hasInitialTabChanged.current = true;
        }
      }
    }
  }, [entityOverlayInitialTabId, displayOrders, displayTrips]);

  useEffect(() => {
    if (!trip) return;
    const orders = trip.orders;

    // TODO Filter orders driver or carrier can see (TBD)?
    orders && setDisplayOrders(orders as unknown as Order[]);
  }, [trip]);

  useEffect(() => {
    // IF OVERLAY RENDERS AN ORDER
    if (!order) return;
    const trips = isDriverOrCarrier ? filterTrips(order?.trips) : order?.trips; // show all if not carrier or driver

    trips && setDisplayTrips(trips);
  }, [order]);

  const filterTrips = (trips: Trip[]): Trip[] => {
    return trips.filter(
      (trip: Trip): boolean =>
        isDriver
          ? trip?.drivers.some((driver) => driver.id === userData?.driver?.id) // filter for driver
          : trip?.carrier?.twuser_id === userData?.id // filter for carrier
    );
  };

  // Show order route / stops
  // Effect to re-format stops order
  useEffect(() => {
    if (!order) return;

    const existingStops =
      order.stops && order.stops.length > 0 ? order.stops : order.full_route_stop_overview;

    const stops = existingStops?.map((stop, index) => {
      return { ...stop, sort_order: index + 1 };
    });

    setStops(stops || []);
  }, [order]);

  // Show trip route / stops
  // Effect to re-format stops order
  useEffect(() => {
    if (!trip) return;

    const stops = trip.stops?.map((stop, index) => {
      return { ...stop, sort_order: index + 1 };
    });

    setStops(stops || []);
  }, [trip]);

  // LIVE TRACKING IF OVERLAY RENDERS AN ORDER
  useEffect(() => {
    if (!order) return;

    if (isDriverOrCarrier || !enableLiveTracking) return;

    if (order && order.order_type === LOAD_TYPE.SIMPLE) {
      setCurrentTruckId(order?.trips[0]?.truck_id);
    } else if (currentTab === 0) {
      // select truck from segment that is in transit for full route tab
      const segmentInTransit = displayTrips?.find(
        (segment) => segment.status === TRIP_STATUS.IN_TRANSIT
      );
      setCurrentTruckId(segmentInTransit?.truck_id);
    } else if (displayTrips && displayTrips.length > 0) {
      // select truck from current segment for segment tab
      const currentSegment = displayTrips[currentTab - 1];
      setCurrentTruckId(currentSegment?.truck_id);
    }
    truck && refetchEquipment();
    setLiveStatus(getLiveStatus(order, currentTab, displayTrips));
    setLiveTrackedEntity(getLiveTrackEntity(currentTab));
  }, [currentTab, order]);

  // LIVE TRACKING IF OVERLAY RENDERS AN ORDER
  useEffect(() => {
    if (!order) return;

    if (isDriverOrCarrier || !enableLiveTracking) return;
    setLiveMarkers([]);
    const truckHasTrackingInfo =
      truck?.asset_eld_id &&
      truck?.eld_connection_id &&
      truck?.latest_reading?.latitude &&
      truck?.latest_reading?.longitude;
    if (!truckHasTrackingInfo) return;

    let showLiveTracking = false;

    if (order?.order_type === LOAD_TYPE.SIMPLE) {
      // for simple load check load status
      if (order?.status === ORDER_STATUS.IN_TRANSIT) showLiveTracking = true;
    } else {
      const isFullRouteTab = currentTab === 0;
      if (isFullRouteTab) {
        // for full route tab check if any segment is in transit
        const segmentInTransit = displayTrips?.find(
          (segment) => segment.status === TRIP_STATUS.IN_TRANSIT
        );
        if (segmentInTransit) {
          showLiveTracking = true;
        }
      } else if (!isFullRouteTab) {
        // for segment tab check if segment is in transit
        if (
          displayTrips &&
          displayTrips.length > 0 &&
          displayTrips[currentTab - 1]?.status === TRIP_STATUS.IN_TRANSIT
        ) {
          showLiveTracking = true;
        }
      }
    }

    showLiveTracking &&
      truck?.latest_reading?.latitude &&
      truck?.latest_reading?.longitude &&
      setLiveMarkers([
        getLiveLocationMarker(
          parseFloat(truck?.latest_reading?.latitude),
          parseFloat(truck?.latest_reading?.longitude),
          DateUtils.getFullLocaleDate(truck?.latest_reading?.truckercloud_reading_timestamp),
          DateUtils.getFullLocaleDate(truck?.latest_reading?.last_attempted_reading_timestamp)
        )
      ]);
  }, [truck, order, currentTab]);

  const { getAndRenderRoute } = useDirections({
    map: map,
    renderOnMap: true,
    renderOptions: {
      polylineOptions: {
        strokeColor: '#7E858E',
        strokeOpacity: 0.5,
        strokeWeight: 4
      },
      suppressMarkers: true
    }
  });

  // Lazy load the getAndRenderRoute function to avoid unnecessary calculations
  const lazyGetAndRenderRoute = useCallback(
    async (directionsRequest: google.maps.DirectionsRequest, usePcMiler: boolean) => {
      if (!getAndRenderRoute) return;

      try {
        // Only render the route if we have displayTrips data
        if (displayTrips && displayTrips.length > 0) {
          await getAndRenderRoute(directionsRequest, usePcMiler);
        }
        // If no displayTrips, skip rendering the route entirely
      } catch (error) {
        console.error('DIRECTIONS RESULT ERROR', error);
        map?.setZoom(3);
      }
    },
    [getAndRenderRoute, displayTrips, map]
  );

  // CUSTOMER INFO FOR ORDER OVERLAY
  useEffect(() => {
    if (!open || !order) return;
    const primaryClient =
      order?.primary_client === CLIENT_TYPE.BROKER ? order?.broker : order?.shipper;
    setPrimaryClient(primaryClient || null);
  }, [open, order]);

  // CUSTOMERS INFO FOR TRIP OVERLAY
  useEffect(() => {
    if (!open || !trip) return;

    const primaryClient =
      trip.orders && trip.orders[0]?.primary_client === CLIENT_TYPE.BROKER
        ? trip.orders && trip.orders[0]?.broker
        : trip.orders && trip.orders[0]?.shipper;
    setPrimaryClient(primaryClient || null);
  }, [open, trip]);

  // on close overlay
  useEffect(() => {
    if (!open) {
      setOrder(null);
      setTrip(null);
      setDisplayTrips([]);
      setDisplayOrders([]);
      setMap(undefined);
      mapRef.current = null;
      setMarkers([]);
      setLiveMarkers([]);
      setCurrentTab(0);
    }
  }, [open]);

  useEffect(() => {
    if (!entityId || !entityType) return;

    if (loadDetailReload && !!entityId && entityType === EntityType.ORDER) {
      getOrderData();
      dispatch(setLoadDetailReload(false));
    } else if (tripDetailReload && !!entityId && entityType === EntityType.TRIP) {
      getTripData();
      dispatch(setTripDetailReload(false));
    }
  }, [loadDetailReload, tripDetailReload, entityId, entityType]);

  // AUTO SELECT TRIP TAB IN ORDER OVERLAY
  useEffect(() => {
    if (selectedTripId && displayTrips && isOrder) {
      const preselectedSegment = displayTrips.find((segment) => segment.id === selectedTripId);
      if (preselectedSegment) {
        const ix = isDriver
          ? displayTrips.indexOf(preselectedSegment)
          : displayTrips.indexOf(preselectedSegment) + 1;
        setCurrentTab(ix);
        onSelectedTripChange && onSelectedTripChange(selectedTripId);
        dispatch(setSelectedTripId(undefined));
      }
    }
  }, [selectedTripId, displayTrips, isOrder]);

  /**
   * A re-usable function to set map for given stops
   * */
  const setMapMarkers = (stops: TripStop[], startOrder?: number): Promise<void> => {
    return (async (): Promise<void> => {
      if (stops && stops.length <= MAX_STOP_MARKER_COUNT) {
        const markers: LocationMarker[] = getMapMarkers(
          stops[0]?.location || undefined,
          stops[stops.length - 1]?.location || undefined,
          stops
            .slice(1, -1)
            .filter((s) => !!s && !!s.location)
            .map((s) => s.location) as LoadLocation[],
          (startOrder ? startOrder : stops[0]?.sort_order + 1) ?? 1
        );

        if (markers.length >= 2) {
          setMarkers(markers);

          // start is first marker
          const start = markers[0];
          // end is last marker
          const end = markers[markers.length - 1];
          // stops are all markers except start and end
          const stops = markers.slice(1, markers.length - 1);

          // Render route
          const directionsRequest: google.maps.DirectionsRequest = {
            avoidFerries: true,
            origin: start.position,
            destination: end.position,
            travelMode: google.maps.TravelMode.DRIVING,
            unitSystem: google.maps.UnitSystem.IMPERIAL
          };

          if (stops.length > 0) {
            directionsRequest.waypoints = stops.map((s) => ({
              location: s.position,
              stopover: true
            }));
          }

          if (getAndRenderRoute) {
            try {
              // Use the lazy function instead of direct call
              await lazyGetAndRenderRoute(directionsRequest, !usesPcMiler);
            } catch (error) {
              console.error('DIRECTIONS RESULT ERROR', error);
              map?.setZoom(3);
            }
          }
        }
      }
    })();
  };

  const handleDrawerClose = (): void => {
    dispatch(setEntityOverlayInitialTabId(null));
    hasInitialTabChanged.current = false;
    onClose && onClose();
  };

  const handleTabChange = (
    _event: React.SyntheticEvent,
    newValue: string,
    clickFromTab: boolean
  ): void => {
    if (clickFromTab) {
      setCurrentTab(parseInt(newValue));
      return;
    }
    let tabsToUse: any = [];
    if (isOrder && displayTrips.length > 0) {
      tabsToUse = displayTrips;
    } else if (isTrip && displayOrders.length > 0) {
      tabsToUse = displayOrders;
    }

    const tabToAdd = isOrder ? { id: order?.id } : { id: trip?.id };

    isDriver ? (tabsToUse = [...tabsToUse, tabToAdd]) : (tabsToUse = [tabToAdd, ...tabsToUse]);

    const index = tabsToUse.findIndex((tab: any) => tab.id === newValue);
    setCurrentTab(index);
  };

  const openScanner = (): void => {
    setIsTwScannerOpen(true);
    // close BOL upload modal if scanner was opened from BOL upload
    setUploadBolOpen(false);
  };

  return (
    <Drawer
      open={open}
      anchor="right"
      onClose={handleDrawerClose}
      PaperProps={{
        className: 'load-detail__main'
      }}
    >
      <HeaderOverlay
        entityType={entityType || EntityType.ORDER}
        readableId={isOrder && order?.readable_id ? order?.readable_id : trip?.readable_id || ''}
        client={primaryClient?.name || ''}
        currentTab={currentTab}
        loadStatus={order?.status}
        tripStatus={trip?.status}
        ediLoadTenderId={order?.edi_load_tender_id}
        truck={truck}
        displaySegments={displayTrips}
        onClose={handleDrawerClose}
      />
      <StyledTabsContainer>
        <OverlayTabs
          displaySegments={isOrder && displayTrips.length > 0 ? displayTrips : []}
          displayOrders={isTrip && displayOrders.length > 0 ? displayOrders : []}
          entityTypeView={entityType}
          orderReadableId={order?.readable_id || ''}
          isFilteredView={isDriver}
          currentTab={currentTab}
          onTabChange={handleTabChange}
        />
      </StyledTabsContainer>
      {enableLiveTracking &&
        matchesMobileOrTablet &&
        !enableHideGoogleMapsAndRoutingCalculations && (
          <TwLiveTrackingAlert
            status={liveStatus}
            equipment={truck}
            entityType={liveTrackedEntity}
            timestamp={
              truck?.latest_reading?.truckercloud_reading_timestamp
                ? DateUtils.getFullLocaleDate(truck?.latest_reading?.truckercloud_reading_timestamp)
                : undefined
            }
          />
        )}
      {googleMapsLoaded && showMap && !enableHideGoogleMapsAndRoutingCalculations && (
        <TwMap
          map={map}
          setMap={setMap}
          mapRef={mapRef}
          className="load-detail__map"
          center={DEFAULT_MAP_CENTER}
          zoom={2}
          disableDefaultUI
          mapTypeControl={enableLiveTracking ? true : false}
          mapTypeControlOptions={{
            mapTypeIds: ['roadmap', 'satellite', 'terrain'],
            position: google.maps.ControlPosition.TOP_LEFT,
            style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR
          }}
          showReloadButton={
            enableLiveTracking && !isDriverOrCarrier && liveMarkers && liveMarkers.length > 0
          }
          onReload={(): Promise<void> => {
            return new Promise((resolve) => {
              refetchEquipment()
                .catch(() => {
                  addNotification({
                    message: "Couldn't fetch new location. Please try again.",
                    variant: 'error'
                  });
                })
                .finally(() => {
                  resolve();
                });
            });
          }}
        >
          {markers.map((marker, index) => (
            <TwMarker
              key={marker.order || index}
              map={map}
              position={marker.position}
              icon={getMarkerIcon(marker.type)}
              label={{
                color: '#FFFFFF',
                fontSize: '12px',
                fontWeight: '500',
                text: marker?.order?.toString() || '',
                fontFamily: 'Poppins'
              }}
            />
          ))}

          {!isDriverOrCarrier &&
            liveMarkers.map((marker, index) => (
              <TwMarker
                key={markers.length + index + 1}
                map={map}
                position={marker.position}
                icon={getMarkerIcon(marker.type)}
                timestamp={marker.timestamp}
                lastUpdatedTimestamp={marker.lastUpdatedTimestamp}
                type={LocationMarkerType.LIVE_LOCATION}
              />
            ))}
        </TwMap>
      )}

      {isOrder && order && displayTrips && (
        <TabPanel
          value={currentTab}
          key={isDriver ? displayTrips?.length : 0}
          index={isDriver ? displayTrips?.length || 1 : 0}
          id={`tw-order-detail-panel-${isDriver ? displayTrips?.length || 1 : 0}`}
        >
          <OrderTab
            order={order || null}
            primaryClient={primaryClient}
            readonly={readonly || isCustomerContact}
            forceOnlyEdit={forceOnlyEdit}
            reloadData={getOrderData}
            changeTab={handleTabChange}
            handleMarkers={setMapMarkers}
          />
        </TabPanel>
      )}
      {isTrip && trip && !isTripLoading && (
        <TabPanel
          value={currentTab}
          key={isDriver ? displayOrders?.length : 0}
          index={isDriver ? displayOrders?.length : 0}
          id={`tw-order-detail-panel-${isDriver ? displayOrders?.length || 1 : 0}`}
        >
          <TripTab
            segmentData={trip || null}
            loadData={order || null}
            primaryClient={primaryClient}
            readonly={readonly || isCustomerContact}
            reloadData={getTripData}
            segmentOrder={trip.sort_order}
            stops={stops}
            changeTab={handleTabChange}
            onStatusChange={onTripStatusChange}
            forceOnlyEdit={forceOnlyEdit}
            handleMarkers={setMapMarkers}
            open={open}
          />
        </TabPanel>
      )}
      {isOrder &&
        order &&
        displayTrips &&
        displayTrips.map((trip, index): JSX.Element => {
          return (
            <TabPanel
              value={currentTab}
              key={isDriver ? index : index + 1} // for non filtered view first tab is `full route tab` hence + 1
              index={isDriver ? index : index + 1}
              id={`tw-trip-detail-${isDriver ? index : index + 1}`}
            >
              <TripTab
                segmentData={{ ...trip, load_id: order.id }}
                loadData={order || null}
                primaryClient={primaryClient}
                readonly={readonly || isCustomerContact}
                reloadData={(entity, tripId): void =>
                  entity && entity === 'trip' && tripId ? getTripData(tripId) : getOrderData()
                }
                segmentOrder={trip.sort_order}
                stops={stops}
                changeTab={handleTabChange}
                onStatusChange={onOrderStatusChange}
                forceOnlyEdit={forceOnlyEdit}
                handleMarkers={setMapMarkers}
                open={open}
              />
            </TabPanel>
          );
        })}
      {isTrip &&
        trip &&
        !isTripLoading &&
        displayOrders &&
        displayOrders.map((order, index): JSX.Element => {
          return (
            <TabPanel
              value={currentTab}
              key={isDriver ? index : index + 1} // for non filtered view first tab is `full route tab` hence + 1
              index={isDriver ? index : index + 1}
              id={`tw-trip-detail-${isDriver ? index : index + 1}`}
            >
              <OrderTab
                order={order || null}
                primaryClient={primaryClient}
                readonly={readonly || isCustomerContact}
                forceOnlyEdit={forceOnlyEdit}
                reloadData={(): void =>
                  entityType === EntityType.TRIP ? getTripData() : getOrderData(order.id)
                }
                changeTab={handleTabChange}
                handleMarkers={setMapMarkers}
              />
            </TabPanel>
          );
        })}

      <Backdrop
        className="load-detail__general-loader"
        sx={{
          color: '#fff',
          position: 'absolute',
          zIndex: 10000,
          top: 0,
          right: 0
        }}
        open={isOrderLoading || isTripLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

      {matchesMobileOrTablet && order && isRequireBolActive && isDriverOrCarrier && (
        <UploadBol
          open={uploadBolOpen}
          onClose={(): void => setUploadBolOpen(false)}
          loadId={order.id}
          onSuccess={getOrderData}
          customUploadHandler={
            matchesMobileOrTablet && enableDynamicWebTwain ? openScanner : undefined
          }
        />
      )}
    </Drawer>
  );
};

export default TwEntityOverlay;
